import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import BusinessLink from "@/components/BusinessLink";
import { businessUrl } from "@/lib/businessUrl";
import { motion } from "framer-motion";
import { Search, MapPin, Star, Filter, X, List, Map as MapIcon, Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="glass sticky top-0 z-50 border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight">lokalnie.pro</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/szukaj" className="text-sm font-medium text-foreground">
              Katalog firm
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground">
                  Panel
                </Link>
                <Button variant="ghost" onClick={logout}>Wyloguj</Button>
              </>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost">Zaloguj</Button></Link>
                <Link to="/rejestracja">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white">Rozpocznij</Button>
                </Link>
              </>
            )}
          </nav>

          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <nav className="flex flex-col gap-3">
              <Link to="/szukaj" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Katalog firm
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                    Panel
                  </Link>
                  <Button variant="ghost" onClick={() => { logout(); setMobileMenuOpen(false); }}>
                    Wyloguj
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full">Zaloguj</Button>
                  </Link>
                  <Link to="/rejestracja" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white">Rozpocznij</Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
      />
    ))}
  </div>
);

const FilterPanel = ({ categories, cities, filters, setFilters, onApply }) => (
  <div className="space-y-6">
    <div>
      <label className="text-sm font-medium mb-2 block">Kategoria</label>
      <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
        <SelectTrigger data-testid="filter-category">
          <SelectValue placeholder="Wszystkie kategorie" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Wszystkie kategorie</SelectItem>
          {categories.filter(Boolean).map((cat) => (
            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <label className="text-sm font-medium mb-2 block">Miasto</label>
      <Select value={filters.city || "all"} onValueChange={(v) => setFilters({ ...filters, city: v === "all" ? "" : v })}>
        <SelectTrigger data-testid="filter-city">
          <SelectValue placeholder="Wszystkie miasta" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Wszystkie miasta</SelectItem>
          {cities.filter(Boolean).map((city) => (
            <SelectItem key={city} value={city}>{city}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <label className="text-sm font-medium mb-3 block">
        Minimalna ocena: {filters.minRating || 0}
      </label>
      <Slider
        value={[filters.minRating || 0]}
        onValueChange={([v]) => setFilters({ ...filters, minRating: v })}
        max={5}
        step={0.5}
        className="w-full"
        data-testid="filter-rating"
      />
    </div>

    <Button onClick={onApply} className="w-full" data-testid="apply-filters-btn">
      Zastosuj filtry
    </Button>
  </div>
);

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 20 });
  
  const [filters, setFilters] = useState({
    query: searchParams.get('query') || '',
    category: searchParams.get('category') || '',
    city: searchParams.get('city') || '',
    minRating: parseFloat(searchParams.get('min_rating')) || 0
  });

  const [searchInput, setSearchInput] = useState(filters.query);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [catRes, cityRes] = await Promise.all([
          axios.get(`${API}/businesses/categories`),
          axios.get(`${API}/businesses/cities`)
        ]);
        setCategories(catRes.data);
        setCities(cityRes.data);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [searchParams]);

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('query')) params.append('query', searchParams.get('query'));
      if (searchParams.get('category')) params.append('category', searchParams.get('category'));
      if (searchParams.get('city')) params.append('city', searchParams.get('city'));
      if (searchParams.get('min_rating')) params.append('min_rating', searchParams.get('min_rating'));
      const currentPage = parseInt(searchParams.get('page')) || 1;
      const currentLimit = parseInt(searchParams.get('limit')) || 20;
      params.append('page', currentPage.toString());
      params.append('limit', currentLimit.toString());

      const response = await axios.get(`${API}/businesses?${params.toString()}`);
      // Backend returns {items, total, page, pages, limit}; older deploys plain array
      if (Array.isArray(response.data)) {
        setBusinesses(response.data);
        setPagination({ total: response.data.length, page: 1, pages: 1, limit: response.data.length });
      } else {
        setBusinesses(response.data.items || []);
        setPagination({
          total: response.data.total || 0,
          page: response.data.page || 1,
          pages: response.data.pages || 1,
          limit: response.data.limit || 20,
        });
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchInput) {
      newParams.set('query', searchInput);
    } else {
      newParams.delete('query');
    }
    setSearchParams(newParams);
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams();
    if (filters.query) newParams.set('query', filters.query);
    if (filters.category) newParams.set('category', filters.category);
    if (filters.city) newParams.set('city', filters.city);
    if (filters.minRating > 0) newParams.set('min_rating', filters.minRating.toString());
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({ query: '', category: '', city: '', minRating: 0 });
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };

  const mapCenter = businesses.length > 0 
    ? [businesses[0].latitude, businesses[0].longitude]
    : [52.2297, 21.0122]; // Warsaw default

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Szukaj firmy, usługi..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-12 h-11"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-btn">Szukaj</Button>
          </form>

          <div className="flex gap-2">
            {/* Mobile Filter Button */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-btn">
                  <Filter className="w-4 h-4 mr-2" />
                  Filtry
                </Button>
              </SheetTrigger>
              <SheetContent side="left">
                <SheetHeader>
                  <SheetTitle>Filtry</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterPanel
                    categories={categories}
                    cities={cities}
                    filters={filters}
                    setFilters={setFilters}
                    onApply={applyFilters}
                  />
                </div>
              </SheetContent>
            </Sheet>

            {/* View Mode Toggle */}
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-r-none"
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-l-none"
                onClick={() => setViewMode('map')}
                data-testid="view-map-btn"
              >
                <MapIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Active Filters */}
        {(searchParams.get('query') || searchParams.get('category') || searchParams.get('city') || searchParams.get('min_rating')) && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-sm text-slate-500">Aktywne filtry:</span>
            {searchParams.get('query') && (
              <Badge variant="secondary" className="gap-1">
                Szukaj: {searchParams.get('query')}
                <X className="w-3 h-3 cursor-pointer" onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.delete('query');
                  setSearchParams(p);
                  setSearchInput('');
                }} />
              </Badge>
            )}
            {searchParams.get('category') && (
              <Badge variant="secondary" className="gap-1">
                {searchParams.get('category')}
                <X className="w-3 h-3 cursor-pointer" onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.delete('category');
                  setSearchParams(p);
                }} />
              </Badge>
            )}
            {searchParams.get('city') && (
              <Badge variant="secondary" className="gap-1">
                {searchParams.get('city')}
                <X className="w-3 h-3 cursor-pointer" onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.delete('city');
                  setSearchParams(p);
                }} />
              </Badge>
            )}
            {searchParams.get('min_rating') && (
              <Badge variant="secondary" className="gap-1">
                Min. ocena: {searchParams.get('min_rating')}
                <X className="w-3 h-3 cursor-pointer" onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.delete('min_rating');
                  setSearchParams(p);
                }} />
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              Wyczyść wszystko
            </Button>
          </div>
        )}

        <div className="flex gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Filtry</h3>
                <FilterPanel
                  categories={categories}
                  cities={cities}
                  filters={filters}
                  setFilters={setFilters}
                  onApply={applyFilters}
                />
              </CardContent>
            </Card>
          </aside>

          {/* Results */}
          <main className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">
                Znaleziono <strong>{businesses.length}</strong> firm
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <div className="aspect-video bg-slate-200" />
                    <CardContent className="p-4 space-y-3">
                      <div className="h-5 bg-slate-200 rounded w-3/4" />
                      <div className="h-4 bg-slate-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : viewMode === 'list' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {businesses.map((business, index) => (
                  <motion.div
                    key={business.business_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <BusinessLink business={business} data-testid={`business-${business.business_id}`}>
                      <Card className="card-hover overflow-hidden group h-full">
                        <div className="aspect-video relative overflow-hidden">
                          <img
                            src={business.photos?.[0] || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800'}
                            alt={business.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <Badge className="absolute top-3 left-3 bg-white/90 text-slate-700">
                            {business.category}
                          </Badge>
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-amber-600 transition-colors">
                            {business.name}
                          </h3>
                          <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{business.address}, {business.city}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <StarRating rating={Math.round(business.rating)} />
                              <span className="text-sm font-medium">{business.rating}</span>
                            </div>
                            <span className="text-xs text-slate-400">{business.reviews_count} opinii</span>
                          </div>
                        </CardContent>
                      </Card>
                    </BusinessLink>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="h-[600px] rounded-xl overflow-hidden border">
                <MapContainer
                  center={mapCenter}
                  zoom={6}
                  className="h-full w-full"
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {businesses.map((business) => (
                    <Marker
                      key={business.business_id}
                      position={[business.latitude, business.longitude]}
                    >
                      <Popup>
                        <div className="p-1">
                          <h3 className="font-semibold">{business.name}</h3>
                          <p className="text-sm text-slate-500">{business.category}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            <span className="text-sm">{business.rating}</span>
                          </div>
                          {(() => {
                            const u = businessUrl(business);
                            return u.external ? (
                              <a href={u.url} className="text-sm text-amber-600 hover:underline mt-2 block">
                                Zobacz szczegóły →
                              </a>
                            ) : (
                              <Link to={u.url} className="text-sm text-amber-600 hover:underline mt-2 block">
                                Zobacz szczegóły →
                              </Link>
                            );
                          })()}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            )}

            {!loading && businesses.length === 0 && (
              <div className="text-center py-16">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Brak wyników</h3>
                <p className="text-slate-500 mb-4">Spróbuj zmienić kryteria wyszukiwania</p>
                <Button variant="outline" onClick={clearFilters}>
                  Wyczyść filtry
                </Button>
              </div>
            )}

            {/* Pagination — shown only in list view when there's more than 1 page */}
            {!loading && pagination.pages > 1 && viewMode === 'list' && (
              <div className="flex items-center justify-between mt-8 flex-wrap gap-3">
                <div className="text-sm text-slate-500">
                  Strona <strong className="text-slate-900">{pagination.page}</strong> z {pagination.pages}
                  {' '}— {pagination.total} firm laczne
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={pagination.page <= 1}
                    onClick={() => {
                      const np = new URLSearchParams(searchParams);
                      np.set('page', '1');
                      setSearchParams(np);
                    }}>« Pierwsza</Button>
                  <Button variant="outline" size="sm" disabled={pagination.page <= 1}
                    onClick={() => {
                      const np = new URLSearchParams(searchParams);
                      np.set('page', (pagination.page - 1).toString());
                      setSearchParams(np);
                    }}>‹ Poprzednia</Button>
                  {(() => {
                    // Window of up to 5 numeric page buttons centered on current page
                    const half = 2;
                    let start = Math.max(1, pagination.page - half);
                    let end = Math.min(pagination.pages, start + 4);
                    start = Math.max(1, end - 4);
                    const pages = [];
                    for (let p = start; p <= end; p++) pages.push(p);
                    return pages.map(p => (
                      <Button key={p} variant={p === pagination.page ? 'default' : 'outline'} size="sm"
                        className={p === pagination.page ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                        onClick={() => {
                          const np = new URLSearchParams(searchParams);
                          np.set('page', p.toString());
                          setSearchParams(np);
                        }}>{p}</Button>
                    ));
                  })()}
                  <Button variant="outline" size="sm" disabled={pagination.page >= pagination.pages}
                    onClick={() => {
                      const np = new URLSearchParams(searchParams);
                      np.set('page', (pagination.page + 1).toString());
                      setSearchParams(np);
                    }}>Nastepna ›</Button>
                  <Button variant="outline" size="sm" disabled={pagination.page >= pagination.pages}
                    onClick={() => {
                      const np = new URLSearchParams(searchParams);
                      np.set('page', pagination.pages.toString());
                      setSearchParams(np);
                    }}>Ostatnia »</Button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
